const INK = '#1D1D1F';
const SECONDARY = '#6E6E73';
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

// Fixed-size artwork stays sharp in a feed, even when saved from a narrow phone.
export async function exportReferralImage(node, content) {
  if (!node || !content.link) throw new Error('Invitation is not ready');
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1080;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Image export is unavailable');
  context.fillStyle = '#F5F5F7';
  context.fillRect(0, 0, 1080, 1080);

  const write = (text, x, y, size, lineHeight, width, weight = 400, color = INK) => {
    context.fillStyle = color;
    context.font = `${weight} ${size}px ${FONT}`;
    context.textBaseline = 'top';
    let line = '';
    for (const word of String(text).split(/\s+/)) {
      const candidate = line ? `${line} ${word}` : word;
      if (line && context.measureText(candidate).width > width) {
        context.fillText(line, x, y);
        y += lineHeight;
        line = '';
      }
      // Long referral URLs must wrap without losing attribution characters.
      for (const character of (line ? ` ${word}` : word)) {
        if (line && context.measureText(line + character).width > width) {
          context.fillText(line, x, y);
          y += lineHeight;
          line = '';
        }
        line += character;
      }
    }
    if (line) { context.fillText(line, x, y); y += lineHeight; }
    return y;
  };

  const logo = node.querySelector('img');
  if (logo) {
    if (!logo.complete && typeof logo.decode === 'function') await logo.decode();
    if (logo.naturalWidth) context.drawImage(logo, 80, 80, 64, 64);
  }
  write('Gab AI', 164, 95, 38, 46, 800, 600);
  let y = write(content.headline, 80, 228, 76, 88, 880, 600) + 26;
  y = write(content.benefits, 80, y, 30, 42, 920, 400, SECONDARY) + 64;
  if (content.reward) {
    y = write(content.reward, 80, y, 56, 68, 920, 600) + 14;
    y = write(content.condition, 80, y, 28, 40, 900, 400, SECONDARY) + 38;
  }
  y = Math.max(750, y);
  context.fillStyle = '#D8D8DC';
  context.fillRect(80, y, 920, 2);
  y = write(content.linkLabel, 80, y + 30, 24, 32, 920, 600) + 12;
  y = write(content.link, 80, y, 25, 34, 920) + 20;
  if (content.reward) y = write(content.disclosure, 80, y, 22, 30, 920, 400, SECONDARY);
  if (y > 1040) throw new Error('Invitation text is too long for the image');

  const blob = await new Promise((resolve, reject) => canvas.toBlob(
    value => value ? resolve(value) : reject(new Error('Could not create invitation image')), 'image/png',
  ));
  const filename = 'gab-ai-invitation.png';
  const file = typeof File === 'function' ? new File([blob], filename, { type: 'image/png' }) : null;
  const mobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
    || ((navigator.maxTouchPoints || 0) > 1 && window.innerWidth < 768);
  if (mobile && file && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'Gab AI invitation' });
      return;
    } catch (error) {
      if (error?.name === 'AbortError') return;
    }
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
